import { useState, useEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger, 
  DialogFooter 
} from "@/components/ui/dialog"
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { Coffee, Plus, Edit, Trash2, Star, Trophy, Heart, Upload, Image, Crown, Award, Target } from "lucide-react"
import { DashboardLayout } from "@/components/layout/DashboardLayout"

interface RewardTemplate {
  id: string
  name: string
  description: string
  reward_type: 'discount' | 'free_item' | 'points' | 'special'
  reward_value: string
  image_url?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export default function VisitRewardTemplates() {
  const { user, loading: authLoading } = useAuth()
  const { toast } = useToast()
  const [templates, setTemplates] = useState<RewardTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<RewardTemplate | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [formData, setFormData] = useState<{
    name: string
    description: string
    reward_type: 'discount' | 'free_item' | 'points' | 'special'
    reward_value: string
    is_active: boolean
  }>({
    name: "",
    description: "",
    reward_type: "discount",
    reward_value: "",
    is_active: true
  })

  useEffect(() => {
    if (!authLoading) {
      fetchTemplates()
    }
  }, [authLoading])

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('reward_templates')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      
      setTemplates((data as RewardTemplate[]) || [])
    } catch (error) {
      console.error('Error fetching templates:', error)
      toast({
        title: "Fehler",
        description: "Besucherprämien-Vorlagen konnten nicht geladen werden.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (file: File): Promise<string | null> => {
    if (!user?.id) return null

    try {
      setUploading(true)
      
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${Date.now()}.${fileExt}`
      
      const { data, error } = await supabase.storage
        .from('reward-images')
        .upload(fileName, file)

      if (error) throw error

      const { data: urlData } = supabase.storage
        .from('reward-images')
        .getPublicUrl(data.path)

      return urlData.publicUrl
    } catch (error) {
      console.error('Error uploading image:', error)
      toast({
        title: "Fehler",
        description: "Bild konnte nicht hochgeladen werden.",
        variant: "destructive",
      })
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleSaveTemplate = async () => {
    if (!user?.id) return

    try {
      let imageUrl = editingTemplate?.image_url || null

      // Upload image if selected
      if (selectedImage) {
        imageUrl = await handleImageUpload(selectedImage)
      }

      const templateData = {
        name: formData.name,
        description: formData.description,
        reward_type: formData.reward_type,
        reward_value: formData.reward_value,
        is_active: formData.is_active,
        image_url: imageUrl,
      }

      if (editingTemplate) {
        // Update existing template
        const { error } = await supabase
          .from('reward_templates')
          .update(templateData)
          .eq('id', editingTemplate.id)

        if (error) throw error
        
        toast({
          title: "Erfolg",
          description: "Besucherprämien-Vorlage wurde aktualisiert.",
        })
      } else {
        // Create new template
        const { error } = await supabase
          .from('reward_templates')
          .insert(templateData)

        if (error) throw error
        
        toast({
          title: "Erfolg",
          description: "Neue Besucherprämien-Vorlage wurde erstellt.",
        })
      }

      await fetchTemplates()
      resetForm()
      setDialogOpen(false)
    } catch (error) {
      console.error('Error saving template:', error)
      toast({
        title: "Fehler",
        description: "Besucherprämien-Vorlage konnte nicht gespeichert werden.",
        variant: "destructive",
      })
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from('reward_templates')
        .delete()
        .eq('id', templateId)

      if (error) throw error

      await fetchTemplates()
      toast({
        title: "Erfolg",
        description: "Besucherprämien-Vorlage wurde gelöscht.",
      })
    } catch (error) {
      console.error('Error deleting template:', error)
      toast({
        title: "Fehler",
        description: "Besucherprämien-Vorlage konnte nicht gelöscht werden.",
        variant: "destructive",
      })
    }
  }

  const handleToggleActive = async (templateId: string) => {
    try {
      const template = templates.find(t => t.id === templateId)
      if (!template) return

      const { error } = await supabase
        .from('reward_templates')
        .update({ is_active: !template.is_active })
        .eq('id', templateId)

      if (error) throw error

      await fetchTemplates()
      toast({
        title: "Erfolg",
        description: "Vorlagenstatus wurde geändert.",
      })
    } catch (error) {
      console.error('Error toggling template status:', error)
      toast({
        title: "Fehler",
        description: "Status konnte nicht geändert werden.",
        variant: "destructive",
      })
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      reward_type: "discount",
      reward_value: "",
      is_active: true
    })
    setEditingTemplate(null)
    setSelectedImage(null)
  }

  const openEditDialog = (template: RewardTemplate) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      description: template.description,
      reward_type: template.reward_type,
      reward_value: template.reward_value,
      is_active: template.is_active
    })
    setDialogOpen(true)
  }

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'discount': return <Star className="h-4 w-4" />
      case 'free_item': return <Coffee className="h-4 w-4" />
      case 'points': return <Trophy className="h-4 w-4" />
      case 'special': return <Crown className="h-4 w-4" />
      default: return <Heart className="h-4 w-4" />
    }
  }

  const getRewardTypeLabel = (type: string) => {
    switch (type) {
      case 'discount': return 'Rabatt'
      case 'free_item': return 'Gratis Artikel'
      case 'points': return 'Punkte'
      case 'special': return 'Spezial'
      default: return type
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 space-y-6">
          <h1 className="text-3xl font-bold">Besucherprämien-Vorlagen verwalten</h1>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Lade Vorlagen...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Besucherprämien-Vorlagen verwalten</h1>
            <p className="text-muted-foreground">
              Erstellen Sie Vorlagen für Besucherprämien, die Administratoren als Ausgangspunkt nutzen können
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Neue Vorlage
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingTemplate ? 'Besucherprämien-Vorlage bearbeiten' : 'Neue Besucherprämien-Vorlage erstellen'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name der Vorlage</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Gratis Kaffee nach 10 Besuchen"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Beschreiben Sie die Vorlage für Besucherprämien..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Belohnungstyp</Label>
                    <Select value={formData.reward_type} onValueChange={(value: any) => setFormData(prev => ({ ...prev, reward_type: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="discount">Rabatt</SelectItem>
                        <SelectItem value="free_item">Gratis Artikel</SelectItem>
                        <SelectItem value="points">Punkte</SelectItem>
                        <SelectItem value="special">Spezial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="value">Beispielwert</Label>
                    <Input
                      id="value"
                      value={formData.reward_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, reward_value: e.target.value }))}
                      placeholder={formData.reward_type === 'discount' ? 'z.B. 10%' : 'z.B. Gratis Kaffee'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">Bild (optional)</Label>
                  <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                    {editingTemplate?.image_url && !selectedImage ? (
                      <div className="space-y-2">
                        <img 
                          src={editingTemplate.image_url} 
                          alt="Template" 
                          className="w-24 h-24 object-cover rounded-lg mx-auto"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.createElement('input')
                            input.type = 'file'
                            input.accept = 'image/*'
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0]
                              if (file) setSelectedImage(file)
                            }
                            input.click()
                          }}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Bild ändern
                        </Button>
                      </div>
                    ) : selectedImage ? (
                      <div className="space-y-2">
                        <img 
                          src={URL.createObjectURL(selectedImage)} 
                          alt="Preview" 
                          className="w-24 h-24 object-cover rounded-lg mx-auto"
                        />
                        <Button
                          variant="outline"
                          onClick={() => setSelectedImage(null)}
                        >
                          Entfernen
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Image className="h-12 w-12 mx-auto text-muted-foreground" />
                        <div className="text-sm text-muted-foreground">
                          <Button
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) setSelectedImage(file)
                              }
                              input.click()
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Bild hochladen
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="active">Vorlage ist aktiv</Label>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSaveTemplate} disabled={uploading}>
                  {uploading ? 'Speichere...' : editingTemplate ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <Coffee className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gesamt Vorlagen</p>
                  <p className="text-2xl font-bold">{templates.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-500/10 p-3 rounded-lg">
                  <Star className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Aktive Vorlagen</p>
                  <p className="text-2xl font-bold">
                    {templates.filter(t => t.is_active).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <Trophy className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Belohnungstypen</p>
                  <p className="text-2xl font-bold">
                    {new Set(templates.map(t => t.reward_type)).size}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates List */}
        <Card>
          <CardHeader>
            <CardTitle>Alle Besucherprämien-Vorlagen</CardTitle>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-8">
                <Coffee className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Noch keine Besucherprämien-Vorlagen</h3>
                <p className="text-muted-foreground mb-4">
                  Erstellen Sie Ihre erste Vorlage für Besucherprämien
                </p>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Erste Vorlage erstellen
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <Card key={template.id} className="border">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-2">
                          {getRewardIcon(template.reward_type)}
                          <h3 className="font-medium">{template.name}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={template.is_active ? "default" : "secondary"}>
                            {template.is_active ? "Aktiv" : "Inaktiv"}
                          </Badge>
                        </div>
                      </div>
                      
                      {template.image_url && (
                        <img 
                          src={template.image_url} 
                          alt={template.name}
                          className="w-full h-32 object-cover rounded-lg mb-4"
                        />
                      )}
                      
                      <p className="text-sm text-muted-foreground mb-2">
                        {template.description}
                      </p>
                      
                      <div className="flex items-center justify-between mb-4">
                        <Badge variant="outline">
                          {getRewardTypeLabel(template.reward_type)}
                        </Badge>
                        <span className="text-sm font-medium">
                          {template.reward_value}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(template)}
                          className="flex-1"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Bearbeiten
                        </Button>
                        <Switch
                          checked={template.is_active}
                          onCheckedChange={() => handleToggleActive(template.id)}
                        />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Vorlage löschen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Sind Sie sicher, dass Sie diese Besucherprämien-Vorlage löschen möchten? 
                                Diese Aktion kann nicht rückgängig gemacht werden.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteTemplate(template.id)}>
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}